import { ProductsTableProps } from './products-table.types'
import styles from './products-table.module.scss'
// import cl from 'classnames'
import {
  TableContainer,
  TableHeader,
  TableHeaderProps,
  TableBodyContainer,
  TableBodyLine,
  TableBodyLineProps,
  PaginationButtons,
  ImageSquare,
  Text1,
  formatPrice,
  parseStringToNumberAndApplyOnChange
} from '@touche-lts/panel-ui/dist'
import { productsTableWidths } from './products-table.utils'
import { ProductsTableSortProps } from 'mobx-stores/utils'
import { observer } from 'mobx-react-lite'
import { ProductsTableEntityDTO } from 'models'
import { useRootStore } from 'mobx-stores/store-provider'
import moment from 'moment'
import { useGetScrollWidth } from 'hooks/custom-hooks'

const ProductsTableComponent = (props: ProductsTableProps) => {
  const {
    containerProps,
    isColumnMarked,
    getSortDirection,
    handleSortDirectionChange,
    searchProps,
    onSearchPropsChange,
    searchResults,
    paginationInfo,
    onPageSelect,
    hideStoreColumn
  } = props

  const {
    generalStore: { showError }
    // routingStore: { push }
  } = useRootStore()

  const {scrollX, handleScroll} = useGetScrollWidth()
  const scrollbarSpacing = 52

  const tableColumns: TableHeaderProps['tableColumns'] = [
    {
      label: 'id',
      widthPx: productsTableWidths.id,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ID),
      sortDirection: getSortDirection(ProductsTableSortProps.ID),
      setSortDirection: handleSortDirectionChange(ProductsTableSortProps.ID),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.id,
        onChange: parseStringToNumberAndApplyOnChange({
          showError,
          onChange: id => onSearchPropsChange({ id })
        })
      }
    },
    {
      label: 'product',
      isColumnMarked: isColumnMarked(ProductsTableSortProps.PRODUCT_NAME),
      sortDirection: getSortDirection(ProductsTableSortProps.PRODUCT_NAME),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.PRODUCT_NAME
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.productName,
        onChange: productName => onSearchPropsChange({ productName })
      },
      widthPx: hideStoreColumn ? productsTableWidths.product : productsTableWidths.product - productsTableWidths.store
    },
    {
      label: 'store',
      isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME),
      sortDirection: getSortDirection(ProductsTableSortProps.STORE_NAME),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.STORE_NAME
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.storeName,
        onChange: storeName => onSearchPropsChange({ storeName })
      }, 
      widthPx: hideStoreColumn ? productsTableWidths.store : productsTableWidths.store + scrollbarSpacing ,
    },
    {
      label: 'active skus',
      widthPx: productsTableWidths.activeSkus,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ACTIVE_SKUS),
      sortDirection: getSortDirection(ProductsTableSortProps.ACTIVE_SKUS),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.ACTIVE_SKUS
      ),
      sliderFilterProps: {
        label: 'active skus',
        min: 0,
        max: 100,
        value: [searchProps.minSKUs, searchProps.maxSKUs],
        onChange: ([minSKUs, maxSKUs]) =>
          onSearchPropsChange({ minSKUs, maxSKUs })
      }
    },
    {
      label: 'inventory',
      widthPx: productsTableWidths.inventory,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.INVENTORY),
      
    },
    {
      label: 'sold',
      widthPx: productsTableWidths.sold,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.SOLD),
      sortDirection: getSortDirection(ProductsTableSortProps.SOLD),
      setSortDirection: handleSortDirectionChange(ProductsTableSortProps.SOLD),
      sliderFilterProps: {
        label: 'items sold',
        min: 0,
        max: 100,
        value: [searchProps.minSold, searchProps.maxStock],
        onChange: ([minSold, maxStock]) =>
          onSearchPropsChange({ minSold, maxStock })
      }
    },
    {
      label: 'revenue',
      widthPx: productsTableWidths.revenue,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.REVENUE),
      sortDirection: getSortDirection(ProductsTableSortProps.REVENUE),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.REVENUE
      ),
      sliderFilterProps: {
        label: 'usd',
        min: 0,
        max: 100,
        value: [searchProps.minRevenue, searchProps.maxRevenue],
        onChange: ([minRevenue, maxRevenue]) =>
          onSearchPropsChange({ minRevenue, maxRevenue })
      }
    },
    {
      label: 'department',
      widthPx: productsTableWidths.department,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.DEPARTMENT),
      sortDirection: getSortDirection(ProductsTableSortProps.DEPARTMENT),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.DEPARTMENT
      ),
      inputProps: {
        placeholder: 'Search...',
        value: searchProps.departmentName,
        onChange: departmentName => onSearchPropsChange({ departmentName })
      }
    },
    {
      label: 'added to marketplace',
      widthPx: productsTableWidths.addedToMarketplace,
      isColumnMarked: isColumnMarked(ProductsTableSortProps.ADDED_DATE),
      sortDirection: getSortDirection(ProductsTableSortProps.ADDED_DATE),
      setSortDirection: handleSortDirectionChange(
        ProductsTableSortProps.ADDED_DATE
      ),
      periodFilterProps: {
        period: searchProps.addedOnPeriod,
        setPeriod: addedOnPeriod => onSearchPropsChange({ addedOnPeriod }),
        className: styles.periodFilter
      }
    }
  ]
  if (hideStoreColumn) {
    tableColumns.splice(2, 1)
  }

  const getBodyLineCells = (
    product: ProductsTableEntityDTO
  ): TableBodyLineProps['cells'] => {
    const cells: TableBodyLineProps['cells'] = [
      {
        widthPx: productsTableWidths.id,
        children: product.id,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ID)
      },
      {
        // widthPercentage: 100,
        widthPx: hideStoreColumn ? productsTableWidths.product : productsTableWidths.product - productsTableWidths.store,
        children: (
          <>
            <ImageSquare src={product.image?.url} width={30} />
            <Text1 bold overflowEllipsis style={{ marginLeft: 8 }}>
              {product.name}
            </Text1>
          </>
        ),
        isColumnMarked: isColumnMarked(ProductsTableSortProps.PRODUCT_NAME)
      },
      {
        // widthPercentage: 100,
        widthPx: hideStoreColumn ? productsTableWidths.store : productsTableWidths.store + scrollbarSpacing ,
        children: <Text1 overflowEllipsis>{product.storeName}</Text1>,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.STORE_NAME)
      },
      {
        widthPx: productsTableWidths.activeSkus,
        children: product.activeSkuCount,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ACTIVE_SKUS)
      },
      {
        widthPx: productsTableWidths.inventory,
        children: (
          <div>
            <Text1 overflowEllipsis>{product.stock || 0} in stock</Text1>
            <Text1 overflowEllipsis>
              ({product.variants || 0} variant
              {product.variants === 1 ? '' : 's'})
            </Text1>
          </div>
        ),
        isColumnMarked: isColumnMarked(ProductsTableSortProps.INVENTORY)
      },
      {
        widthPx: productsTableWidths.sold,
        children: product.sold || 0,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.SOLD)
      },
      {
        widthPx: productsTableWidths.revenue,
        children: formatPrice(product.revenue),
        isColumnMarked: isColumnMarked(ProductsTableSortProps.REVENUE)
      },
      {
        widthPx: productsTableWidths.department,
        children: product.department,
        isColumnMarked: isColumnMarked(ProductsTableSortProps.DEPARTMENT)
      },
      {
        widthPx: productsTableWidths.addedToMarketplace,
        children: moment(product.addedToStore).format('DD.MM.YY'),
        isColumnMarked: isColumnMarked(ProductsTableSortProps.ADDED_DATE)
      }
    ]
    if (hideStoreColumn) {
      cells.splice(2, 1)
    }

    return cells
  }

  return (
    <TableContainer {...containerProps} style={{overflowX: 'auto', marginTop: 10}} className={styles.thinScroll} onScroll={handleScroll}>
      <TableHeader tableColumns={tableColumns} />
      <TableBodyContainer className={styles.tableBodyContainer}>
        {searchResults?.map(product => {
          return (
            <TableBodyLine key={product.id} cells={getBodyLineCells(product)} />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons
        alwaysShow
        {...paginationInfo}
        onPageSelect={onPageSelect}
        style={{width : scrollX}}
      />
    </TableContainer>
  )
}

const ProductsTable = observer(ProductsTableComponent)
export { ProductsTable }
